import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NotificationService } from '../../services/notification';
import { FilteredSaleOrder, LoadManagementService } from '../../services/load-management.service';
import { SalesOrderService } from '../../services/sales-order';
import { Customer, CustomerService } from '../../services/customer';
import { SelectedProduct } from '../../services/products';
import { CartComponent } from '../../components/cart-component/cart-component';
import { SalesOrdersTableComponent } from '../../components/sales-orders-table-component/sales-orders-table-component';
import { AuthService } from '../../services/auth-service';
import { RouteSelectionService } from '../../services/route-selection';

@Component({
  selector: 'app-un-assigned-loads-component',
  standalone: true,
  templateUrl: './un-assigned-loads-component.html',
  styleUrls: ['./un-assigned-loads-component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatCheckboxModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    DragDropModule,
    CartComponent,
    SalesOrdersTableComponent
  ],
  providers: [DatePipe],
})
export class UnAssignedLoadsComponent implements OnInit {
  loadSalesOrders: FilteredSaleOrder[] = [];
  allSelected: boolean = false;
  grandTotal = 0;

  // Cart view states
  showCart = false;
  selectedCustomerFromList: Customer | null = null;
  selectedProducts: SelectedProduct[] = [];
  startDate = '';
  endDate = '';
  note: string = '';

  constructor(
    private loadService: LoadManagementService,
    private notificationService: NotificationService,
    private salesOrderService: SalesOrderService,
    private customerService: CustomerService,
    private authService: AuthService,
    private routeSelection: RouteSelectionService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.loadOrders(false);
    this.authService.getCurrentUser().subscribe(u => {
      const route = this.routeSelection.getSelected();
      const routeName = route ? '-' + route.route : '';
      this.note = `${u?.user}-${this.datePipe.transform(new Date(), 'dd/MM/yy')}${routeName}`;
    })
  }

  async loadOrders(updateState: boolean): Promise<boolean> {
    this.grandTotal = 0;

    const payload = {
      has_load: false,
      startDate: this.startDate,
      endDate: this.endDate,
    };

    try {
      const res = await firstValueFrom(this.loadService.filterSaleOrders(payload));

      if (res.status !== 'success' || !res.orders) {
        this.notificationService.showMessage('Failed loading sales orders.');
        this.loadSalesOrders = [];
        return false;
      }

      const orders = res.orders;
      this.grandTotal = orders.reduce((sum, o) => sum + o.totalAmount, 0);

      this.loadSalesOrders = orders; 

      return true;
    } catch (err) {
      console.error('Error loading orders:', err);
      this.notificationService.showMessage('Error loading sales orders');
      return false;
    }
  }


  async applyFilter() {
    await this.loadOrders(true);
  }

  createLoad() {
    if (!this.loadSalesOrders.some(o => o.selected)) {
      this.notificationService.showMessage('Select at least one order.');
      return;
    }

    const payload = {
      sale_order_ids: this.loadSalesOrders.filter(o => o.selected).map((order, i) => ({
        id: order.id,
        sequence: (i + 1)
      })),
      note: this.note || undefined
    };

    this.loadService.createLoad(payload).subscribe(res => {
      if (res.status === 'success') {
        this.notificationService.showMessage(`Created Load ${res.name || res.load_id}`);
        this.loadOrders(false);
      } else {
        this.notificationService.showMessage(`Failed: ${res.message || ''}`);
      }
    });
  }  

  async openOrder(order: any): Promise<void> {
    try {
      const res = await firstValueFrom(this.salesOrderService.getSalesOrderById(order.id));
      if (res.status !== 'success') {
        this.notificationService.showMessage(res.message || 'Failed to fetch order');
        return;
      }

      const details = res.order;
      if (!details) return;

      this.selectedProducts = details.lines.map(line => ({
        productId: line.product_id,
        quantity: line.quantity,
        price: line.price_unit
      }));

      const customers = await firstValueFrom(this.customerService.get());
      this.selectedCustomerFromList = customers.find(c => c.id === details.customerId) ?? null;

      this.showCart = true;
    } catch (err) {
      console.error('Error loading sales order:', err);
      this.notificationService.showMessage('Error loading sales order');
    }
  }

  onOrdersChange(orders: any[]) {
  }
}
